import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import OverlayDialog from "../OverlayDialog.jsx";
import ConfirmDialog from "../ConfirmDialog.jsx";
import Select from "../Select.jsx";
import SectionActions, { SECTION_BUTTON } from "../settings/SectionActions.jsx";
import Button from "../ui/Button.jsx";
import Field from "../ui/Field.jsx";
import Notice, { ErrorText } from "../ui/Notice.jsx";
import Panel from "../ui/Panel.jsx";
import { Step, Steps } from "../ui/Steps.jsx";
import { SkeletonBar } from "../ui/Skeleton.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../../lib/authTokenStore.js";
import { hasSensitivePermission, invalidateMyPermissions, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { estimateGrantImpact, groupKeyId } from "../../lib/accessClient.js";
import { INPUT, NOTE, labelFor, shortLabelFor, groupName, recipientName, compactScope, recipientKey, CandidatePicker, keyOnly } from "./ActivityConfigurationFields.jsx";
import {
    ACTIVITY_CAPABILITIES, RAW_PROMPT_CAPABILITY, activityConfigurationError, activityConfigurationMutation,
    listActivityGrants, uncertainActivityMutation,
} from "../../lib/activityConfiguration.js";

const TAG = "inline-flex shrink-0 items-center rounded-[5px] border px-[6px] py-[1px] text-[12px] leading-[1.4]";

export default function ActivityAccessTab() {
    const identity = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    return <ActivityGrants key={identity} />;
}

function activityEnforcement(caps) {
    if (caps?.loading) return "loading";
    if (caps?.degraded || caps?.subjectScopeDegraded) return "unknown";
    switch (caps?.subjectScope?.disposition) {
        case "legacy_passthrough": return "off";
        case "unrestricted":
        case "scoped":
        case "empty":
        case "viewer_unmapped":
        case "no_active_generation": return "on";
        default: return "unknown";
    }
}

function EnforcementPill({ enforcement }) {
    return <span role="status" aria-busy={enforcement === "loading"}
        className={`${TAG} border-[color:var(--line)] text-[color:var(--muted)]`}>
        {enforcement === "off" ? "Enforcement off"
            : enforcement === "on" ? "Enforcement on"
                : enforcement === "loading" ? "Checking enforcement" : "Enforcement unknown"}
    </span>;
}

/// Shared by the open scope step and the collapsed scope summary, so a single
/// estimate serves both. Returns null while inactive, which also discards a
/// preview that is still in flight when the scope stops being group-based.
function useScopeImpact({ recipient, recipientKind, groups, active }) {
    const [result, setResult] = useState(null);
    const definition = JSON.stringify({
        grantee: recipientKind === "viewer" ? { type: "viewer", viewerUserId: recipient?.viewerUserId }
            : { type: "group", directoryGroup: recipient ? keyOnly(recipient) : null },
        scopeGroups: groups.map(keyOnly),
    });
    useEffect(() => {
        if (!active) return undefined;
        let current = true;
        const timer = setTimeout(() => {
            estimateGrantImpact(JSON.parse(definition)).then(value => {
                if (current) setResult({ ...value, definition });
            }).catch(() => { if (current) setResult({ definition, degraded: true }); });
        }, 180);
        return () => { current = false; clearTimeout(timer); };
    }, [definition, active]);
    return active && result?.definition === definition ? result : null;
}

const STEP_LABELS = { capability: "What they can see", scope: "Whose activity", expiry: "When it ends" };
const STEP_ORDER = ["capability", "scope", "expiry"];
const RECIPIENT_KINDS = [{ value: "viewer", label: "Individual user" }, { value: "group", label: "Directory group members" }];

// One step of the editor: open, it holds its controls; closed, it says its
// answer with a Change button.
function GrantStep({ name, answered, value, open, onOpen, children }) {
    return <Step number={STEP_ORDER.indexOf(name) + 1} state={open ? "current" : answered ? "done" : "todo"}
        title={STEP_LABELS[name]} summary={open ? null : value} collapsed={!open} last={name === "expiry"}
        action={open ? null : <Button size="sm" data-step-edit={name} onClick={onOpen} aria-label={`Change ${name}`}>Change</Button>}
        data-step={name} {...(name === "scope" ? { "data-grant-scope-picker": "" } : {})}>
        <div className="grid gap-[12px]">{children}</div>
    </Step>;
}

export function ActivityGrantEditor({ onSaved, onCancel, enforcement = "unknown" }) {
    const [recipientKind, setRecipientKind] = useState("viewer");
    const [recipient, setRecipient] = useState(null);
    const [capability, setCapability] = useState(ACTIVITY_CAPABILITIES[0].value);
    const [groups, setGroups] = useState([]);
    const [expiryMode, setExpiryMode] = useState("never");
    const [expiry, setExpiry] = useState("");
    const [step, setStep] = useState("capability");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [uncertain, setUncertain] = useState(false);
    const operation = useRef(null);
    const live = useRef(false);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    const scopeChosen = groups.length > 0;
    const valid = Boolean(recipient && scopeChosen);
    const expiryTime = expiryMode === "date" ? new Date(expiry).getTime() : 0;
    const expiryChosen = expiryMode === "never" || (Number.isSafeInteger(expiryTime) && expiryTime > Date.now());
    const expirySummary = expiryMode === "never" ? "No expiry"
        : Number.isSafeInteger(expiryTime) ? new Date(expiryTime).toLocaleString() : "Choose a date";
    const preview = useScopeImpact({ recipient, recipientKind, groups, active: Boolean(recipient) && scopeChosen });
    const scopeSummary = !groups.length ? "Not chosen yet"
        : `${groups.map(groupName).join(", ")}${preview && !preview.degraded ? ` - ${preview.subjectCount} members` : ""}`;
    const save = async event => {
        event.preventDefault();
        if (busy || !valid) return;
        if (!operation.current) {
            const expiresAt = expiryMode === "date" ? new Date(expiry).getTime() : 0;
            if (expiryMode === "date" && (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now())) {
                setStep("expiry");
                setError("Choose an expiry in the future, or choose Never."); return;
            }
            operation.current = activityConfigurationMutation("createGrant", {
                recipient: recipientKind === "viewer" ? { viewerUserId: recipient.viewerUserId } : { directoryGroup: keyOnly(recipient) },
                grant: { capability, scope: { groups: { groups: groups.map(keyOnly) } }, expiresAtUnixMs: String(expiresAt) },
            });
        }
        setBusy(true); setError("");
        try { await operation.current(); if (live.current) onSaved(); }
        catch (failure) {
            if (!live.current) return;
            setUncertain(uncertainActivityMutation(failure));
            setError(activityConfigurationError(failure));
            if (!uncertainActivityMutation(failure)) operation.current = null;
        } finally { if (live.current) setBusy(false); }
    };
    return <form className="grid gap-[16px] text-[13px] text-[color:var(--ink)]" onSubmit={save} aria-busy={busy} aria-label="New activity grant">
        {enforcement === "off" ? <Notice title="Enforcement off">This grant will be prepared without changing current access while enforcement is off.</Notice> : null}
        <fieldset disabled={busy || uncertain} className="m-0 grid min-w-0 gap-[16px] border-0 p-0">
            {!recipient ? <section data-grant-recipient-picker className="grid gap-[12px]">
                <div className="grid gap-[3px]">
                    <h3 className="m-0 text-[14px] font-medium">Who should receive access?</h3>
                    <p className={NOTE}>Choose one user or group. The recipient stays fixed while you define what they can access.</p>
                </div>
                <Field label="Recipient type">
                    <Select ariaLabel="Recipient type" value={recipientKind} onChange={setRecipientKind} options={RECIPIENT_KINDS} />
                </Field>
                <CandidatePicker key={recipientKind} kind={recipientKind} title="Search recipients" selected={new Set()} onSelect={setRecipient} />
            </section> : <>
                <div className="flex flex-wrap items-baseline gap-[8px] rounded-[10px] border border-[color:var(--line)] bg-[color-mix(in_srgb,var(--ink)_5%,transparent)] px-[12px] py-[10px]">
                    <strong className="font-medium">{recipientKind === "group" ? groupName(recipient) : recipient.displayName || "Individual user (name unavailable)"}</strong>
                    <span className="text-[color:var(--muted)]">{enforcement === "off" ? "will be able to" : enforcement === "on" ? "can" : "is configured to"}</span>
                </div>
                <Steps label="New activity grant">
                    <GrantStep name="capability" answered value={labelFor(capability)} open={step === "capability"} onOpen={() => setStep("capability")}>
                        <Field label="Activity capability" hint={ACTIVITY_CAPABILITIES.find(item => item.value === capability).description}>
                            <Select ariaLabel="Activity capability" value={capability} onChange={setCapability} options={ACTIVITY_CAPABILITIES.map(item => ({ value: item.value, label: item.label }))} />
                        </Field>
                        <Button size="sm" className="justify-self-start" onClick={() => setStep("scope")}>Continue</Button>
                    </GrantStep>
                    <GrantStep name="scope" answered={scopeChosen} value={scopeSummary} open={step === "scope"} onOpen={() => setStep("scope")}>
                        <CandidatePicker kind="group" title="Search activity scope groups" selected={new Set(groups.map(groupKeyId))} onSelect={group => setGroups(previous => previous.some(item => groupKeyId(item) === groupKeyId(group)) ? previous.filter(item => groupKeyId(item) !== groupKeyId(group)) : previous.length < 32 ? [...previous, group] : previous)} />
                        {groups.length ? <ul className="m-0 flex list-none flex-wrap gap-[6px] p-0">{groups.map(group => <li key={groupKeyId(group)} className={`${TAG} gap-[6px] border-[color:var(--line)] text-[color:var(--ink)]`}>{groupName(group)}
                            <button type="button" className="cursor-pointer border-0 bg-transparent p-0 text-[color:var(--muted)] hover:text-[color:var(--ink)]" aria-label={`Remove ${groupName(group)}`} onClick={() => setGroups(previous => previous.filter(item => groupKeyId(item) !== groupKeyId(group)))}>x</button></li>)}</ul> : null}
                        {groups.length === 32 ? <p className={NOTE}>Up to 32 groups can be selected per grant.</p> : null}
                        {groups.length ? <p className={NOTE} aria-busy={!preview} role="status">{!preview ? "Checking directory scope..."
                            : preview.degraded ? "Directory scope could not be confirmed. Group access depends on current directory membership."
                                : `(${preview.subjectCount} members in scope)`}</p> : null}
                        <Button size="sm" className="justify-self-start" disabled={!scopeChosen} onClick={() => setStep("expiry")}>Continue</Button>
                    </GrantStep>
                    <GrantStep name="expiry" answered={expiryChosen} value={expirySummary} open={step === "expiry"} onOpen={() => setStep("expiry")}>
                        <div className="flex flex-wrap gap-[16px]">
                            <label className="flex items-center gap-[8px]"><input type="radio" name="grant-expiry" value="never" checked={expiryMode === "never"} onChange={() => { setExpiryMode("never"); setExpiry(""); setError(""); }} />Never</label>
                            <label className="flex items-center gap-[8px]"><input type="radio" name="grant-expiry" value="date" checked={expiryMode === "date"} onChange={() => { setExpiryMode("date"); setError(""); }} />On a date</label>
                        </div>
                        {expiryMode === "date" ? <input className={`${INPUT} justify-self-start`} aria-label="Expires at (local time)" type="datetime-local" required value={expiry} onChange={event => { setExpiry(event.target.value); setError(""); }} /> : null}
                    </GrantStep>
                </Steps>
            </>}
        </fieldset>
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-[8px]">
            <Button disabled={busy || uncertain} onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy || !valid}>{busy ? "Saving grant..." : uncertain ? "Retry saving grant" : "Save activity grant"}</Button>
        </div>
    </form>;
}


function RevokeGrant({ row, onSaved }) {
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [uncertain, setUncertain] = useState(false);
    const operation = useRef(null);
    const live = useRef(false);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    const revoke = async () => {
        if (busy) return;
        operation.current ??= activityConfigurationMutation("revokeGrant", { grantId: row.id, expectedRevision: row.revision });
        setBusy(true); setError("");
        try { await operation.current(); if (live.current) onSaved(); }
        catch (failure) {
            if (!live.current) return;
            setError(activityConfigurationError(failure)); setUncertain(uncertainActivityMutation(failure));
            if (!uncertainActivityMutation(failure)) operation.current = null;
        } finally { if (live.current) setBusy(false); }
    };
    // An uncertain revoke can only be retried: the same operation, not a new one.
    const cancel = () => { if (busy || uncertain) return; setConfirm(false); setError(""); };
    return <>
        <Button size="sm" onClick={() => setConfirm(true)}>Revoke grant</Button>
        <ConfirmDialog
            open={confirm}
            analyticsId={DIALOG.ACCESS_REVOKE}
            eyebrow={recipientName(row.recipient)}
            title="Revoke activity grant?"
            body={<div className="grid gap-[8px]" aria-busy={busy}>
                <p className={NOTE}>{labelFor(row.grant?.capability)} over {compactScope(row.grant?.scope)}. Other activity grants may still give access.</p>
                <ErrorText>{error}</ErrorText>
            </div>}
            confirmLabel={busy ? "Revoking..." : uncertain ? "Retry" : "Revoke"}
            confirmDisabled={busy}
            destructive
            onConfirm={revoke}
            onCancel={cancel}
        />
    </>;
}

/// Collapses a page of grants under one heading per recipient, preserving the
/// order the server returned. Pagination is per grant, so one recipient's
/// grants can still land on separate pages.
function groupByRecipient(grants = []) {
    const order = [];
    const index = new Map();
    for (const row of grants) {
        const key = recipientKey(row.recipient);
        if (!index.has(key)) { index.set(key, order.length); order.push({ key, recipient: row.recipient, rows: [] }); }
        order[index.get(key)].rows.push(row);
    }
    return order;
}

function ActivityGrants() {
    const caps = useMyPermissions();
    const enforcement = activityEnforcement(caps);
    const canManage = hasSensitivePermission(caps, PERMISSIONS.GRANTS_MANAGE);
    const canChooseRecipients = hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const [afterId, setAfterId] = useState("");
    const [reload, setReload] = useState(0);
    const [result, setResult] = useState(null);
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState("");
    const query = JSON.stringify([afterId, reload]);
    const page = result?.query === query ? result : null;
    useEffect(() => {
        let current = true;
        listActivityGrants(afterId).then(value => { if (current) setResult({ ...value, query }); })
            .catch(() => { if (current) setResult({ query, error: true }); });
        return () => { current = false; };
    }, [afterId, query]);
    const saved = message => { setEditing(false); setMessage(message); setReload(value => value + 1); invalidateMyPermissions(); };
    return <div className="flex min-w-0 flex-1 flex-col gap-[16px] text-[13px] text-[color:var(--ink)]">
        <SectionActions>
            {canManage && canChooseRecipients && !editing ? <button type="button" className={SECTION_BUTTON} onClick={() => { setEditing(true); setMessage(""); }}>New activity grant</button> : null}
            <button type="button" className={SECTION_BUTTON} disabled={!page} onClick={() => setReload(value => value + 1)}>Refresh grants</button>
        </SectionActions>
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
            <EnforcementPill enforcement={enforcement} />
            {enforcement === "off" ? <p className={NOTE}>These grants are prepared for enforcement. Current access still follows users' roles.</p> : null}
            {enforcement === "unknown" ? <p role="alert" className={NOTE}>Current enforcement could not be confirmed. These grants may not reflect current access.</p> : null}
        </div>
        {canManage && !canChooseRecipients ? <p className={NOTE}>Choosing recipients requires directory browsing permission.</p> : null}
        {message ? <Notice tone="ok"><span role="status">{message}</span></Notice> : null}
        <OverlayDialog open={editing && canManage && canChooseRecipients} onClose={() => setEditing(false)}
            analyticsId={DIALOG.ACTIVITY_GRANT} eyebrow="Activity grants" title="New activity grant" width="600px" maxWidth="96vw" dismissible={false}>
            {editing ? <ActivityGrantEditor enforcement={enforcement} onCancel={() => setEditing(false)} onSaved={() => saved(enforcement === "off"
                ? "Activity grant prepared. Current access is unchanged while enforcement is off."
                : enforcement === "on" ? "Activity grant saved." : "Activity grant saved. Enforcement status could not be confirmed.")} /> : null}
        </OverlayDialog>
        <section className="grid min-w-0 gap-[12px]" aria-label="Configured activity grants" aria-busy={!page}>
            {!page ? <GrantsLoading /> : page.error ? <Notice tone="error" title="Activity grants could not be loaded."
                action={<Button size="sm" onClick={() => setReload(value => value + 1)}>Retry</Button>} /> : <>
                {!page.grants?.length ? <p className={`${NOTE} py-[8px]`}>{afterId ? "No activity grants on this page." : "No activity grants yet."}</p> : null}
                {groupByRecipient(page.grants).map(entry => <Panel key={entry.key} as="section" data-grant-recipient
                    title={recipientName(entry.recipient)} meta={entry.rows.length === 1 ? "1 grant" : `${entry.rows.length} grants`}>
                    <div className="grid min-w-0">{entry.rows.map(row => {
                        const revoked = Number(row.revokedAtUnixMs) > 0;
                        const expiry = Number(row.grant?.expiresAtUnixMs);
                        const expired = expiry > 0 && expiry <= Date.now();
                        const ended = revoked || expired;
                        const directoryInactive = row.inactiveNotPrimaryDirectory === true && !ended;
                        const raw = row.grant?.capability === RAW_PROMPT_CAPABILITY;
                        return <div key={`${row.id}:${row.revision}`} data-activity-grant-id={row.id}
                            className={`flex flex-wrap items-center gap-[8px] border-t border-[color:var(--line-soft)] py-[8px] sm:grid sm:grid-cols-[11rem_minmax(0,1fr)_auto_auto] sm:gap-[12px] ${ended ? "opacity-60" : ""}`}>
                            <span title={labelFor(row.grant?.capability)}
                                className={`${TAG} justify-self-start ${raw ? "border-[color:var(--line-strong)] font-medium text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--muted)]"}`}>{shortLabelFor(row.grant?.capability)}</span>
                            <span className="min-w-0 truncate" title={compactScope(row.grant?.scope, 32)}>{compactScope(row.grant?.scope)}</span>
                            <span className="text-[12px] text-[color:var(--muted)]">
                                {ended ? <span className={`${TAG} border-[color:var(--line)]`}>{revoked ? "Revoked" : "Expired"}</span>
                                    : directoryInactive ? <span className={`${TAG} border-[color:var(--line)] text-[color:var(--ink)]`}>Inactive: not primary directory</span>
                                    : expiry > 0 ? `until ${new Date(expiry).toLocaleDateString()}` : null}
                            </span>
                            <span className="justify-self-end">
                                {canManage && !ended ? <RevokeGrant row={row} onSaved={() => saved(enforcement === "off"
                                    ? "Prepared activity grant revoked. Current role-based access is unchanged while enforcement is off."
                                    : enforcement === "on" ? "Activity grant revoked. Other activity grants may still provide access."
                                        : "Activity grant revoked. Enforcement status could not be confirmed.")} /> : null}
                            </span>
                            {directoryInactive ? <p className={`${NOTE} w-full sm:col-span-4`}>This grant gives no access. Changing the primary directory may reactivate it; revoke it to prevent future access.</p> : null}
                        </div>;
                    })}</div>
                </Panel>)}
            </>}
            {afterId || page?.nextAfterId ? <div className="flex gap-[8px]">
                {afterId ? <Button size="sm" disabled={!page} onClick={() => setAfterId("")}>First page</Button> : null}
                {page?.nextAfterId ? <Button size="sm" onClick={() => setAfterId(page.nextAfterId)}>Next page</Button> : null}
            </div> : null}
        </section>
    </div>;
}

// First load: two recipient cards in the shape the list renders.
function GrantsLoading() {
    return <>
        <span className="sr-only">Loading grants...</span>
        {[0, 1].map(card => <div key={card} aria-hidden="true" className="grid gap-[10px] rounded-[10px] border border-[color:var(--line)] p-[14px]" style={{ opacity: card ? 0.6 : 1 }}>
            <SkeletonBar className="w-[180px]" />
            <SkeletonBar className="w-[60%]" />
        </div>)}
    </>;
}
